import { useCallback, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { GroupBase } from 'react-select'
import { useApolloClient } from '@apollo/client'
import { debounce } from 'lodash-es'

import { Field } from 'Components/Field/Field'
import { FieldTypeEnum } from 'Components/Field/Field.types'
import { SelectOption } from 'Components/Select/Select.types'

import {
  getAvailablePhones,
  getAvailablePhonesVariables,
} from 'Constants/graphql/queries/listing/__generated__/getAvailablePhones'
import { GET_AVAILABLE_DEVICES } from 'Constants/graphql/queries/listing/GetDevices'

import { ReplacementDeviceSelectProps } from './ReplacementDeviceSelect.types'

export const ReplacementDeviceSelect = <
  Option extends SelectOption,
  IsMulti extends boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: ReplacementDeviceSelectProps<Option, IsMulti, Group>,
) => {
  const apolloClient = useApolloClient()
  const [lastOptions, setLastOptions] = useState<SelectOption[]>([])
  const { depotId, rentalId, ...rest } = props

  const promiseOptions = useCallback(
    async (inputValue: string) => {
      const response = await apolloClient.query<
        getAvailablePhones,
        getAvailablePhonesVariables
      >({
        query: GET_AVAILABLE_DEVICES,
        variables: {
          depotID: depotId,
          rentalID: rentalId,
          searchterm: inputValue,
        },
      })

      const stocklistItems = response?.data.rental.replacements?.stocklistItems
      const parsedResponse: SelectOption[] =
        stocklistItems?.map(stocklistItem => {
          const { deviceDefinition } = stocklistItem
          const label = `${deviceDefinition?.name} IMEI ${stocklistItem?.imei}`

          return {
            label,
            value: stocklistItem.id.toString(),
          }
        }) ?? []

      return new Promise<SelectOption[]>(resolve => {
        setLastOptions(parsedResponse)

        resolve(parsedResponse)
      })
    },
    [apolloClient, depotId, rentalId],
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadOptions = useCallback(
    debounce((inputText, callback) => {
      promiseOptions(inputText).then(options => callback(options))
    }, 300),
    [promiseOptions],
  )

  const defaultNoOptionsMessage = ({ inputValue }: { inputValue: string }) => {
    if (inputValue.length < 2) {
      return (
        <FormattedMessage
          id="GlobalSearch_InputValidationMessage"
          values={{ limit: 3 }}
        />
      )
    }

    return <FormattedMessage id="No_devices_found" />
  }

  return (
    <Field
      {...rest}
      placeholder="Device Id, Apple iPhone 8, Samsung Galaxy S9..."
      selectProps={{
        isClearable: true,
        isMulti: false,
        lastOptions,
        loadOptions,
        noOptionsMessage: defaultNoOptionsMessage,
      }}
      type={FieldTypeEnum.selectAsync}
    />
  )
}
